import React, { Component } from 'react';
import { Modal, Select, Spin, Alert, Table } from 'antd';
import numeral from 'numeral';
import Create from 'components/Create';
import api from 'api';
// eslint-disable-next-line
const debug = require('debug')('app:ContractCreate:');

// The create-screen preview shows the same rate rows createContract will seed: they come from
// the chosen type's contract_type_rates (fetched below), NOT a hardcoded labor-type list.

function fmtCurrency(v) {
    return (v === null || v === undefined || v === '') ? '—' : numeral(parseFloat(v)).format('$0,0.00');
}

// Material discount_pct is stored as a FRACTION (0.20 = 20%), matching the `percentage`
// widget used on the saved-contract view.
function fmtPct(v) {
    return (v === null || v === undefined || v === '') ? '—' : `${parseFloat(v) * 100}%`;
}

// Gate the New Contract flow behind a contract-type picker. On entry to
// /contracts/new we show a modal asking which contract_type to create; picking one
// snapshots that type's default_* columns into the create form (via the standard
// location.state.pushedFormValues prefill mechanism, see components/Form/index.js),
// then renders the normal create form. Cancelling abandons creation entirely and
// routes back to the contracts list -- no record is created.
//
// The createContract RPC re-COALESCEs these same defaults server-side, so the prefill
// is what the user sees/can-edit, not the source of truth; keys here must therefore be
// the caller-overridable params the RPC accepts (procedures/contracts.js createContract).

// contract_types.default_* column -> contract form field key.
function buildPrefillFromType(ct) {
    const prefill = { contract_type: ct.type };

    // Seed the contract's description from the type's (multi-line). This is the value the
    // user edits on the create form and the one generateContractInvoice reads for the
    // line item -- so later edits to the type's description don't retroactively change
    // an existing contract's invoices.
    if (ct.description !== undefined && ct.description !== null) {
        prefill.description = ct.description;
    }

    const map = {
        default_amount: 'contract_amount',
        default_term_months: 'term_months',
        default_renewal_term_months: 'renewal_term_months',
        default_increase_pct: 'auto_renew_increase_pct',
        default_labor_discount_pct: 'labor_discount_pct',
    };

    Object.keys(map).forEach(src => {
        if (ct[src] !== undefined && ct[src] !== null) {
            // pg returns numeric columns as strings ("900.00"); the target form items
            // are numeric (currency/percentage/number), so coerce. Same scale as the
            // backend's COALESCE fallback, so values stay consistent with the type.
            const num = Number(ct[src]);
            prefill[map[src]] = isNaN(num) ? ct[src] : num;
        }
    });

    return prefill;
}

class ContractCreate extends Component {

    state = {
        types: null,
        loading: true,
        error: null,
        selectedType: undefined,
        // Read-only preview of the chosen type's seeded defaults, shown alongside the
        // create form (the interactive labor/discount sub-editors don't render on create).
        previewType: null,
        previewRates: [],
        previewMaterialDiscounts: [],
    };

    componentDidMount() {
        if (this.typeChosen()) {
            return;
        }

        // /enums/contract_types returns every row with all columns (pageLimit -1),
        // so the picker feed already carries default_* -- no per-type follow-up fetch.
        api.get('/enums/contract_types')
            .then(response => {
                if (response.errors) {
                    throw response.errors;
                }
                this.setState({ types: response.body || [], loading: false });
            })
            .catch(error => {
                debug('Failed to load contract types', error);
                this.setState({ error: 'Could not load contract types.', loading: false });
            });
    }

    // A type has been chosen once we've replaced the route with the prefill + marker.
    typeChosen() {
        const { location } = this.props;
        return !!(location.state && location.state.contractTypeChosen);
    }

    handleCancel = () => {
        // Abandon creation; nothing was created. Back to the contracts list.
        this.props.history.push('/contracts');
    };

    handleOk = async () => {
        const { types, selectedType } = this.state;
        if (!selectedType) {
            return;
        }

        const chosen = (types || []).find(ct => ct.type === selectedType) || { type: selectedType };
        const prefill = buildPrefillFromType(chosen);

        // Fetch the type's default rates + material discounts for the read-only create
        // preview. These are snapshotted server-side by createContract (seedContractRates /
        // seedMaterialDiscounts); the create form never binds them (no contract_id yet), so we
        // only preview them here.
        let previewRates = [];
        let previewMaterialDiscounts = [];
        try {
            const rateResp = await api.get(`/contract_type_rates?contract_type=${encodeURIComponent(selectedType)}`);
            if (rateResp && !rateResp.errors) {
                previewRates = rateResp.body || [];
            }
        } catch (e) {
            debug('Failed to load rate defaults', e);
        }
        try {
            const resp = await api.get(`/contract_type_material_discounts?contract_type=${encodeURIComponent(selectedType)}`);
            if (resp && !resp.errors) {
                previewMaterialDiscounts = resp.body || [];
            }
        } catch (e) {
            debug('Failed to load material discount defaults', e);
        }

        this.setState({ previewType: chosen, previewRates, previewMaterialDiscounts });

        // Merge on top of anything an upstream flow already seeded (e.g. site.js pushes
        // site_id + bill_to_customer_id when creating a contract from a Site).
        const { location, history } = this.props;
        const existing = (location.state && location.state.pushedFormValues) || {};
        const pushedFormValues = Object.assign({}, existing, prefill);

        history.replace('/contracts/new', { pushedFormValues, contractTypeChosen: true });
    };

    // Read-only preview of the labor rates + material discounts the createContract RPC will
    // seed from the chosen type. The interactive sub-editors (references) are suppressed on
    // create and have nothing to bind to until the contract exists, so without this the create
    // screen showed no rates/discounts at all.
    renderDefaultsPreview() {
        const { previewType, previewRates, previewMaterialDiscounts } = this.state;
        if (!previewType) {
            return null;
        }

        // Rates come straight from the chosen type's contract_type_rates (the exact rows
        // createContract will snapshot), so the preview matches what the server writes.
        const laborRows = (previewRates || []).map(r => ({
            ...r,
            key: r.id || r.rate_type_code,
        }));

        const laborColumns = [
            { title: 'Rate Type', dataIndex: 'rate_type_name', key: 'rate_type_name',
                render: (v, row) => v || row.rate_type_code },
            { title: 'Category', dataIndex: 'rate_type_category', key: 'rate_type_category' },
            { title: 'Regular Rate', dataIndex: 'regular_rate', key: 'regular_rate', render: fmtCurrency },
            { title: 'OT Rate', dataIndex: 'ot_rate', key: 'ot_rate', render: fmtCurrency },
            { title: 'T&M Regular Rate', dataIndex: 'tm_regular_rate', key: 'tm_regular_rate', render: fmtCurrency },
            { title: 'T&M OT Rate', dataIndex: 'tm_ot_rate', key: 'tm_ot_rate', render: fmtCurrency },
        ];

        const discountRows = (previewMaterialDiscounts || []).map(d => ({ ...d, key: d.id || d.vendor_id }));
        const discountColumns = [
            { title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor_name' },
            { title: 'Discount %', dataIndex: 'discount_pct', key: 'discount_pct', render: fmtPct },
        ];

        return (
            <div style={{ maxWidth: '760px', margin: '24px 0 0' }}>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px' }}
                    message="Defaults from contract type"
                    description={`These rates and material discounts come from the "${previewType.type}" contract type and will be seeded onto the contract when you create it. They can be edited afterward on the contract page.`}
                />
                <p style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 8px' }}>Rates</p>
                <Table
                    size="small"
                    bordered
                    pagination={false}
                    columns={laborColumns}
                    dataSource={laborRows}
                    style={{ marginBottom: '24px' }}
                    locale={{ emptyText: 'No rates configured for this type.' }}
                />
                <p style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 8px' }}>Material Discounts</p>
                <Table
                    size="small"
                    bordered
                    pagination={false}
                    columns={discountColumns}
                    dataSource={discountRows}
                    locale={{ emptyText: 'No material discounts configured for this type.' }}
                />
            </div>
        );
    }

    render() {
        if (this.typeChosen()) {
            // customProps from the route are the Contracts `create` config (formActions,
            // formFields, urlFormatter, ...). Create is withRouter and reads the prefill
            // from the (now replaced) location.state.pushedFormValues.
            return (
                <React.Fragment>
                    <Create {...this.props} />
                    {this.renderDefaultsPreview()}
                </React.Fragment>
            );
        }

        const { types, loading, error, selectedType } = this.state;

        return (
            <Modal
                visible={true}
                title="Select Contract Type"
                okText="Continue"
                okButtonProps={{ disabled: !selectedType || loading }}
                onOk={this.handleOk}
                onCancel={this.handleCancel}
                cancelText="Cancel"
                maskClosable={false}
                keyboard={false}
            >
                {error
                    ? <Alert type="error" message={error} showIcon />
                    : loading
                        ? <Spin tip="Loading contract types" />
                        : <div>
                            <p>Which type of contract do you want to create? Its defaults will prefill the new contract.</p>
                            <Select
                                showSearch
                                style={{ width: '100%' }}
                                placeholder="Choose a contract type"
                                optionFilterProp="children"
                                value={selectedType}
                                onChange={value => this.setState({ selectedType: value })}
                            >
                                {(types || []).map(ct => (
                                    <Select.Option key={ct.type} value={ct.type}>
                                        {ct.description ? `${ct.type} — ${ct.description}` : ct.type}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                }
            </Modal>
        );
    }
}

export default ContractCreate;
